import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faCalendarAlt, faUsers, faFileExport, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface UserDto {
    id?: number;
    userName?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
}

interface HostingReportDto {
    workerId: string;
    hostingCost: number;
    uptimeInHours: number;
    cost: number;
    power: number;
    date: string;
}

const HostingReports: React.FC = () => {
    const [users, setUsers] = useState<UserDto[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [generating, setGenerating] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [userFilter, setUserFilter] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [reportData, setReportData] = useState<any[]>([]);
    const [reportFileName, setReportFileName] = useState<string>('');

    // Get default date range (current month)
    const getDefaultDateRange = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        return {
            start: firstDay.toISOString().split('T')[0],
            end: lastDay.toISOString().split('T')[0]
        };
    };

    useEffect(() => {
        const defaultRange = getDefaultDateRange();
        setStartDate(defaultRange.start);
        setEndDate(defaultRange.end);
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/pool/admin/users');
            const userData: UserDto[] = Array.isArray(response.data) ? response.data : [];
            setUsers(userData);
        } catch (err: any) {
            setError('Failed to fetch users: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    // Clean names by removing #cashlab/#CASHLAB
    const cleanName = (name: string): string => {
        if (!name) return name;
        return name.replace(/#cashlab|#CASHLAB/gi, '').trim();
    };

    // Filter users based on search term
    const filteredUsers = users.filter(user => {
        const userName = user.userName || user.name || user.email?.split('@')[0] || '';
        const email = user.email || '';
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';

        const searchTerm = userFilter.toLowerCase();

        return userName.toLowerCase().includes(searchTerm) ||
               email.toLowerCase().includes(searchTerm) ||
               firstName.toLowerCase().includes(searchTerm) ||
               lastName.toLowerCase().includes(searchTerm);
    });

    // Toggle user selection
    const toggleUserSelection = (userName: string) => {
        setSelectedUsers(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(userName)) {
                newSelected.delete(userName);
            } else {
                newSelected.add(userName);
            }
            return newSelected;
        });
    };

    // Select all users
    const selectAllUsers = () => {
        const allUserNames = filteredUsers
            .map(user => user.userName || user.email?.split('@')[0] || `User_${user.id}`)
            .filter(name => name);
        setSelectedUsers(new Set(allUserNames));
    };

    // Clear all selections
    const clearAllSelections = () => {
        setSelectedUsers(new Set());
    };

    // Convert array to CSV
    const arrayToCSV = (data: any[], headers: string[]): string => {
        const csvHeaders = headers.join(',');
        const csvRows = data.map(row =>
            headers.map(header => {
                const value = row[header];
                // Escape commas and quotes in CSV
                if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                    return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
            }).join(',')
        );
        return [csvHeaders, ...csvRows].join('\n');
    };

    // Download CSV file
    const downloadCSV = (csvContent: string, filename: string) => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Generate and show reports in modal
    const generateReports = async () => {
        if (selectedUsers.size === 0) {
            setError('Please select at least one user');
            return;
        }

        if (!startDate || !endDate) {
            setError('Please select both start and end dates');
            return;
        }

        if (new Date(startDate) > new Date(endDate)) {
            setError('Start date must be before end date');
            return;
        }

        setGenerating(true);
        setError('');

        try {
            const allReportsData: any[] = [];

            // Fetch data for each selected user
            for (const userName of Array.from(selectedUsers)) {
                try {
                    const response = await axios.get('/pool/admin/users/uptime', {
                        params: {
                            username: userName,
                            startDate: startDate,
                            endDate: endDate
                        }
                    });

                    const userReports: HostingReportDto[] = Array.isArray(response.data) ? response.data : [];

                    // Add user information to each report entry
                    userReports.forEach(report => {
                        allReportsData.push({
                            userName: cleanName(userName),
                            workerId: cleanName(report.workerId),
                            hostingCost: report.hostingCost?.toFixed(4) || '0.0000',
                            uptimeInHours: report.uptimeInHours?.toFixed(2) || '0.00',
                            power: report.power?.toFixed(2) || '0.00',
                            date: new Date(report.date).toISOString().split('T')[0],
                            totalCost: report.cost?.toFixed(4) || '0.0000'
                        });
                    });
                } catch (userError: any) {
                    console.error(`Failed to fetch data for user ${userName}:`, userError);
                    // Continue with other users even if one fails
                }
            }

            if (allReportsData.length === 0) {
                setError('No data found for the selected users and date range');
                return;
            }

            // Generate filename
            const fromDate = new Date(startDate).toISOString().split('T')[0];
            const toDate = new Date(endDate).toISOString().split('T')[0];
            const filename = `hosting_report_${fromDate}_to_${toDate}.csv`;

            // Set data and show modal
            setReportData(allReportsData);
            setReportFileName(filename);
            setShowModal(true);

        } catch (err: any) {
            setError('Failed to generate reports: ' + (err.response?.data?.message || err.message));
        } finally {
            setGenerating(false);
        }
    };

    // Download CSV from modal
    const downloadFromModal = () => {
        const headers = ['userName', 'workerId', 'hostingCost', 'uptimeInHours', 'power', 'date', 'totalCost'];
        const csvContent = arrayToCSV(reportData, headers);
        downloadCSV(csvContent, reportFileName);
    };

    // Close modal
    const closeModal = () => {
        setShowModal(false);
        setReportData([]);
        setReportFileName('');
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <FontAwesomeIcon icon={faFileExport} className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Hosting Bill Reports</h2>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <p className="mt-1 text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Date Range Selection */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Date Range</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                            Start Date
                        </label>
                        <input
                            type="date"
                            id="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                            End Date
                        </label>
                        <input
                            type="date"
                            id="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 opacity-0">
                            Action
                        </label>
                        <button
                            onClick={generateReports}
                            disabled={generating || selectedUsers.size === 0}
                            className="w-full px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-sm"
                        >
                            {generating ? (
                                <span className="flex items-center justify-center gap-2">
                                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                                    Generating...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <FontAwesomeIcon icon={faFileExport} className="w-4 h-4" />
                                    Generate Report
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* User Selection */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <h3 className="text-lg font-semibold text-gray-900">Select Users ({selectedUsers.size} selected)</h3>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={userFilter}
                                onChange={(e) => setUserFilter(e.target.value)}
                                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={selectAllUsers}
                                    disabled={generating}
                                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    Select All
                                </button>
                                <button
                                    onClick={clearAllSelections}
                                    disabled={generating}
                                    className="px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 disabled:opacity-50"
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center items-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Loading users...</span>
                    </div>
                )}

                {/* Users List */}
                {!loading && filteredUsers.length > 0 && (
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full">
                            <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Select
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User Name
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredUsers.map((user, index) => {
                                    const userName = user.userName || user.email?.split('@')[0] || `User_${user.id}`;
                                    const isSelected = selectedUsers.has(userName);

                                    return (
                                        <tr key={user.id || index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleUserSelection(userName)}
                                                    disabled={generating}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                            </td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                <div className="truncate max-w-48" title={cleanName(userName)}>
                                                    {cleanName(userName)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                <div className="truncate max-w-48" title={user.email || 'No email'}>
                                                    {user.email || 'No email'}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* No Users State */}
                {!loading && filteredUsers.length === 0 && users.length > 0 && (
                    <div className="text-center py-8">
                        <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                        <p className="text-gray-500">No users match your search criteria.</p>
                    </div>
                )}

                {!loading && users.length === 0 && (
                    <div className="text-center py-12">
                        <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Available</h3>
                        <p className="text-gray-500">No users found in the system.</p>
                    </div>
                )}
            </div>

            {/* Report Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-7xl w-full max-h-[90vh] overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Hosting Bill Report ({reportData.length} records)
                            </h3>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={downloadFromModal}
                                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faFileDownload} className="w-4 h-4" />
                                    Download CSV
                                </button>
                                <button
                                    onClick={closeModal}
                                    className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700"
                                >
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="p-4 max-h-[calc(90vh-120px)] overflow-auto">
                            {reportData.length > 0 && (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    User Name
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Worker ID
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Hosting Cost ($/kWh)
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Uptime (Hours)
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Date
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Total Cost ($)
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {reportData.map((record, index) => (
                                                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                        {record.userName}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">
                                                        {record.workerId}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-bold text-red-600">
                                                        ${record.hostingCost}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-blue-600">
                                                        {record.uptimeInHours}h
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">
                                                        {record.date}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-bold text-green-600">
                                                        ${record.totalCost}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Summary section */}
                            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                <h4 className="text-lg font-semibold text-gray-900 mb-3">Summary</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-white p-3 rounded border">
                                        <div className="text-sm text-gray-600">Total Records</div>
                                        <div className="text-xl font-bold text-gray-900">{reportData.length}</div>
                                    </div>
                                    <div className="bg-white p-3 rounded border">
                                        <div className="text-sm text-gray-600">Total Uptime</div>
                                        <div className="text-xl font-bold text-blue-600">
                                            {reportData.reduce((sum, record) => sum + parseFloat(record.uptimeInHours), 0).toFixed(2)}h
                                        </div>
                                    </div>
                                    <div className="bg-white p-3 rounded border">
                                        <div className="text-sm text-gray-600">Total Cost</div>
                                        <div className="text-xl font-bold text-green-600">
                                            ${reportData.reduce((sum, record) => sum + parseFloat(record.totalCost), 0).toFixed(4)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HostingReports;
